import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKidsStore, KidProfile } from '../../store/kidsStore';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'sonner';
import KidSelectorCard from '../../components/kids/KidSelectorCard';

export default function KidSelectorPage() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const { profiles, fetchProfiles, selectKid } = useKidsStore();

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    await fetchProfiles();
  };

  const handleSelectProfile = async (profile: KidProfile) => {
    try {
      await selectKid(profile.id);
      toast.success(`¡Hola ${profile.nombre}! 👋`);

      // Redirigir al home de kids
      navigate('/kids/home');
    } catch (error: any) {
      toast.error(error.message || 'Error al seleccionar perfil');
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        background: tenant.theme.colors.gradients.main,
        fontFamily: tenant.theme.fonts.body.family
      }}
    >
      {/* Formas decorativas de fondo */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute top-10 left-10 text-8xl animate-float">🎮</div>
        <div className="absolute top-20 right-15 text-7xl animate-float delay-2s">⭐</div>
        <div className="absolute bottom-15 left-20 text-9xl animate-float delay-4s">🎨</div>
        <div className="absolute bottom-20 right-10 text-8xl animate-float delay-6s">🚀</div>
        <div className="absolute top-1/2 left-5 text-7xl animate-float delay-8s">🌈</div>
        <div className="absolute top-40 right-5 text-9xl animate-float delay-10s">🎯</div>
      </div>

      <div className="max-w-5xl w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h1
            className="text-6xl font-bold text-white mb-4 animate-bounce"
            style={{
              fontFamily: tenant.theme.fonts.primary.family,
              textShadow: '4px 4px 0 rgba(0,0,0,0.2)'
            }}
          >
            🧠 ¿Quién va a jugar?
          </h1>
          <p
            className="text-2xl text-white font-semibold"
            style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.2)' }}
          >
            Selecciona tu perfil para empezar
          </p>
        </div>

        {/* Grid de perfiles */}
        {profiles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {profiles.map((profile) => (
              <KidSelectorCard
                key={profile.id}
                profile={profile}
                onSelect={handleSelectProfile}
              />
            ))}
          </div>
        ) : (
          /* Empty state */
          <div className="bg-white rounded-3xl p-16 text-center shadow-2xl">
            <div className="text-8xl mb-6">👨‍👩‍👧‍👦</div>
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: tenant.theme.colors.primary }}
            >
              ¡Bienvenido a Neureka Kids!
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Aún no tienes perfiles kids creados.<br />
              Pide a tu papá o mamá que cree un perfil para ti.
            </p>
            <a
              href="/kids"
              className="inline-block px-8 py-4 rounded-3xl font-bold text-lg text-white shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              style={{ background: tenant.theme.colors.primary }}
            >
              👨‍👩‍👧‍👦 Panel de Padres
            </a>
          </div>
        )}

        {/* Link al panel de padres */}
        {profiles.length > 0 && (
          <div className="text-center mt-8">
            <a
              href="/kids"
              className="text-white text-lg font-semibold hover:underline transition-all"
              style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.2)' }}
            >
              👨‍👩‍👧‍👦 Ir a Panel de Padres (gestionar perfiles)
            </a>
          </div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(180deg); }
        }

        .animate-float {
          animation: float 20s ease-in-out infinite;
        }

        .delay-2s { animation-delay: 2s; }
        .delay-4s { animation-delay: 4s; }
        .delay-6s { animation-delay: 6s; }
        .delay-8s { animation-delay: 8s; }
        .delay-10s { animation-delay: 10s; }
      `}</style>
    </div>
  );
}
